import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { ActivityCondition } from "../../../../../models/activity-condition";
import { ActivityConditionType } from "../../../../../models/activity-condition-type";
import { Law } from "../../../../../models/laws";
import { Registration } from "../../../../../models/registration";
import { RegistrationActivityCondition } from "../../../../../models/registration-activity-condition";
import { ActivityConditionControllerService } from "../../../../../services/activity-condition/activity-condition-controller.service";
import { ActivityConditionService } from "../../../../../services/activity-condition/activity-condition.service";
import { RegistrationActivityConditionControllerService } from "../../../../../services/activity-condition/registration-activity-condition-controller.service";
import { ActivityConditionTypeControllerService } from "../../../../../services/classifications-controller/activity-condition-type-controller.service";
import { LawControllerService } from "../../../../../services/laws-controller/law-controller.service";
import { Store } from "../../../../../services/store.service";
import {TranslatedBaseError} from "../../../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: "registration-condition",
  templateUrl: "./activity-condition-registration.component.html",
  providers: [ToastrService],
})
export class RegistrationActivityConditionComponent extends TranslatedBaseError implements OnInit, OnChanges, OnDestroy {

  @Input() public registration: Registration;
  @Input() public serviceId: string;
  @Input() public disabled: boolean;
  @Input() public determinantMapById: { [key: string]: string };
  public registrationActivityConditionList: RegistrationActivityCondition[] = [];
  public registrationActivityConditionListLength: number;
  public activityConditions: ActivityCondition[];
  public showDeterminant: boolean[] = [];

  public registrationActivityCondition: RegistrationActivityCondition;
  public laws: Law[] = [];
  public activityConditionTypes: ActivityConditionType[] = [];
  public searchConditionFilter: any = {
    name: null,
  };

  public activityCondition: any;
  public addNew: boolean = false;
  public showDeterminantBuilder: boolean = false;
  public showList: boolean = false;
  public showActivityConditions = {};

  public subscription: any;

  constructor(private activityConditionControllerService: ActivityConditionControllerService,
              private registrationActivityConditionControllerService: RegistrationActivityConditionControllerService,
              public lawControllerService: LawControllerService,
              public activityConditionTypeControllerService: ActivityConditionTypeControllerService,
              public activityConditionService: ActivityConditionService,
              public store: Store,
              toastr: ToastrService, public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.subscription = this.activityConditionService.update.subscribe((data) => {
      if (data.update) {
        this.loadServiceActivityConditions();
      }
    });
  }

  public ngOnChanges() {
    this.loadServiceActivityConditions();
    this.loadLaws();
    this.loadActivityConditionTypes();
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public dropped(event: CdkDragDrop<RegistrationActivityCondition[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

      // component logic
      if (!this.disabled) {
        this.registrationActivityConditionList.forEach((it, index, itemsArray) => {
          itemsArray[index].sortOrder = index;
        });
        this.sortRegistrationActivityConditionList();
      }
    }
  }

  public loadServiceActivityConditions() {
    if (this.serviceId) {
      this.activityConditionControllerService.getServiceActivityConditions(this.serviceId).subscribe(
        (data) => {
          this.activityConditions = data;
          this.loadRegistrationActivityConditionList();
        },
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
      );
    }
    this.searchConditionFilter.name = null;
  }

  public loadRegistrationActivityConditionList() {
    if (this.registration && this.registration.id) {
      this.registrationActivityConditionControllerService.getServiceRegistrationActivityConditions(this.registration.id).subscribe(
        (data) => {
          const deletedItems = [];
          for (const existingItem of this.registrationActivityConditionList) {
            const keepThis = data.some((regDocCond) => {
              return regDocCond.id === existingItem.id;
            });
            if (!keepThis) { deletedItems.push(existingItem); }
          }
          deletedItems.forEach((item) => {
            this.registrationActivityConditionList.splice(this.registrationActivityConditionList.indexOf(item), 1);
          });
          for (const regDocCond of data) {
            const existingItem = this.registrationActivityConditionList.find((item) => {
              return item.id === regDocCond.id;
            });
            if (existingItem) {
              this.registrationActivityConditionList[this.registrationActivityConditionList.indexOf(existingItem)] = regDocCond;
            } else {
              this.registrationActivityConditionList.push(regDocCond);
            }
          }
          this.updateData(this.registrationActivityConditionList);
        },
        (error) => this.showErrorMessage(error),
      );
      this.searchConditionFilter.name = null;
    }
  }

  public sortRegistrationActivityConditionList() {
    if (!this.disabled) {
      this.registrationActivityConditionControllerService.updateSortOrder(this.registrationActivityConditionList).subscribe(
        (data) => this.responseSuccessful("Registration Conditions reordered!"),
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service", error),
      );
    }
  }

  public loadLaws() {
    this.lawControllerService.getLawsList().subscribe(
      (data) => {
        this.laws = data;
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public loadActivityConditionTypes() {
    this.activityConditionTypeControllerService.getConditionTypes().subscribe(
      (data) => {
        this.activityConditionTypes = data.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
        });
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public getNameOfActivityCondition(registrationActivityCondition) {
    let activityConditionName: any;
    if (this.activityConditions && this.activityConditions.length > 0) {
      this.activityConditions.forEach((thisActivityCondition: any) => {
        if (thisActivityCondition.id === registrationActivityCondition.activityConditionId) {
          activityConditionName = thisActivityCondition.name;
        }
      });
    }
    return activityConditionName;
  }

  public fixVisualEffectOnConditionSelected(activityCondition): void {
    this.showList = false;
    const registrationActivityCondition = new RegistrationActivityCondition();
    registrationActivityCondition.activityConditionId = activityCondition.id;
    this.registrationActivityConditionList.push(registrationActivityCondition);
  }

  public addRegistrationActivityCondition(activityCondition) {
    if (!this.disabled && this.registration !== undefined) {
      this.activityCondition = activityCondition;
      this.registrationActivityCondition = new RegistrationActivityCondition();
      this.registrationActivityCondition.activityConditionId = this.activityCondition.id;

      // for visual effect
      this.fixVisualEffectOnConditionSelected(activityCondition);

      this.registrationActivityConditionControllerService.createActivityCondition(this.registration.id, this.registrationActivityCondition).subscribe(
        () => {
          this.loadServiceActivityConditions();
          this.responseSuccessful("Condition added to registration");
        },
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
      );
    }
  }

  public openEdit(registrationActivityCondition: any, determinantView = false) {
    if (!this.disabled) {
      this.showDeterminantBuilder = determinantView;
      if (registrationActivityCondition) {
        for (let i = this.activityConditions.length - 1; i >= 0; i--) {
          if (this.activityConditions[i].id === registrationActivityCondition.activityConditionId) {
            this.activityCondition = this.activityConditions[i];
          }
        }
        this.registrationActivityCondition = registrationActivityCondition;
        this.addNew = false;
      } else {
        this.activityCondition = new ActivityCondition();
        if (this.searchConditionFilter.name !== null) {
          this.activityCondition.name = this.searchConditionFilter.name;
        }
        this.registrationActivityCondition = new RegistrationActivityCondition();
        this.addNew = true;
      }
      this.updateEditData();
    }
  }

  public hasDeterminant(item) {
    if (!item.jsonDeterminants || item.jsonDeterminants === '[{"type":"OR","items":[]}]') {
      return false;
    }
    return true;
  }

  public removeDeterminant(registrationActivityCondition, index) {
    if (!this.disabled) {
      registrationActivityCondition.jsonDeterminants = "";
      this.registrationActivityConditionControllerService.saveRegistrationActivityCondition(registrationActivityCondition).subscribe(
        (data) => {
          this.showDeterminant[index] = false;
          this.responseSuccessful("Condition Registration updated");
        },
        (error) => this.showErrorMessage(error),
      );
    }
  }

  public updateData(data: any) {
    this.showList = false;
    this.showDeterminant = new Array(data.length).fill(false);
    this.registrationActivityConditionList = data;
    this.registrationActivityConditionListLength = data.length;
    this.toggleRegistrationActivityConditionsFromActivityConditionList();
  }

  public toggleRegistrationActivityConditionsFromActivityConditionList() {
    this.showActivityConditions = {};
    for (let j = this.activityConditions.length - 1; j >= 0; j--) {
      this.showActivityConditions[this.activityConditions[j].id] = true;
      for (let i = this.registrationActivityConditionList.length - 1; i >= 0; i--) {
        if (this.activityConditions[j].id === this.registrationActivityConditionList[i].activityConditionId) {
          this.showActivityConditions[this.activityConditions[j].id] = false;
        }
      }
    }
  }

  public deleteCondition(isConfirmed: boolean, activityConditionId: string) {
    if (!this.disabled && isConfirmed) {
      this.registrationActivityConditionControllerService.deleteRegistrationActivityCondition(activityConditionId).subscribe(
        (data) => {
          this.loadRegistrationActivityConditionList();
          this.responseSuccessful("Condition removed from registration");
        },
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
      );
    }
  }

  public showOrAdd() {
    if (!this.disabled) {
      if (this.activityConditions.length > 0 && this.registrationActivityConditionListLength !== this.activityConditions.length) {
        this.showList = !this.showList;
      } else {
        this.openEdit(false);
      }
    }
  }

  public updateEditData() {
    if (!this.disabled) {
      this.showList = false;
      this.store.state.common.sections.type = "condition";
      this.activityConditionService.serviceId = this.serviceId;
      this.activityConditionService.registration = this.registration;
      this.activityConditionService.activityCondition = JSON.parse(JSON.stringify(this.activityCondition)); // mini-hack so the list doesn't update when working in edit layer
      this.activityConditionService.registrationActivityCondition = JSON.parse(JSON.stringify(this.registrationActivityCondition)); // mini-hack so the list doesn't update when working in edit layer
      this.activityConditionService.laws = this.laws;
      this.activityConditionService.activityConditionTypes = this.activityConditionTypes;
      this.activityConditionService.addNew = this.addNew;
      this.activityConditionService.showDeterminantBuilder = this.showDeterminantBuilder;
      this.activityConditionService.initEdit();
    }
  }

  public searchConditionChanged(value: any): void {
    this.searchConditionFilter = { name: value };
  }
}
